import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faBuilding,
    faSave,
    faTimes,
    faUser,
    faSearch,
    faChevronDown,
    faImage,
} from '@fortawesome/free-solid-svg-icons'
import RequestQueue from '../../api/requestQueue'
import { toast } from 'react-toastify'
import Loading from '../../component/loading'
import Aside from '../../component/aside'
import Navbar from '../../component/navbar'
import { AppContextMenu } from '../../App'

const OrganizationEdit = () => {
    const { updateValue } = useContext(AppContextMenu)
    useEffect(() => {
        updateValue('organization')
    }, [])
    const navigate = useNavigate()
    const { id } = useParams()
    const [loading, setLoading] = useState(false)
    const [users, setUsers] = useState([])

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        supervisor_user_id: '',
        logo: null,
    })
    const [logoPreview, setLogoPreview] = useState('')
    const [userSearch, setUserSearch] = useState('')
    const [showUserDropdown, setShowUserDropdown] = useState(false)

    // Check auth
    useEffect(() => {
        if (!JSON.parse(localStorage.getItem('authResult'))) {
            navigate('/login')
        }
    }, [])

    // Fetch data
    useEffect(() => {
        fetchOrganization()
        fetchUsers()
    }, [])

    const fetchOrganization = async () => {
        setLoading(true)
        await RequestQueue.sendRequest({
            config: {
                method: 'get',
                url: `/organization/${id}`,
            },
            resolve: (res) => {
                setFormData({
                    name: res.data.name,
                    supervisor_user_id: res.data.supervisor_user_id,
                    logo: res.data.logo,
                })
                if (res.data.logo) setLogoPreview(res.data.logo)
                setLoading(false)
            },
            reject: (error) => {
                setLoading(false)
                showError('خطا در دریافت اطلاعات سازمان')
            },
        })
    }

    const fetchUsers = async () => {
        await RequestQueue.sendRequest({
            config: {
                method: 'get',
                url: `/user?page=1&limit=1000000000000`,
            },
            resolve: (res) => {
                setUsers(res.data.data)
                setLoading(false)
            },
            reject: (error) => {
                setLoading(false)
                showError('خطا در دریافت لیست کاربران')
            },
        })
    }

    const showError = (message) => {
        toast.dismiss()
        toast.error(message, { position: 'bottom-center' })
    }

    // Form handlers
    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    const handleLogoChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            setFormData((prev) => ({ ...prev, logo: file }))
            setLogoPreview(URL.createObjectURL(file))
        }
    }

    const handleUserSelect = (user) => {
        setFormData((prev) => ({ ...prev, supervisor_user_id: user.id }))
        setShowUserDropdown(false)
    }

    const handleSubmit = async () => {
        setLoading(true)

        await RequestQueue.sendRequest({
            config: {
                method: 'patch',
                url: `/organization/${id}`,
                data: {
                    name: formData.name,
                    supervisor_user_id: formData.supervisor_user_id,
                },
            },
            resolve: (res) => {
                navigate(-1)
                setLoading(false)
            },
            reject: (error) => {
                setLoading(false)
                showError('خطا در دریافت لیست کاربران')
            },
        })
    }

    // Filter users based on search
    const filteredUsers = users.filter((user) =>
        user.full_name?.toLowerCase().includes(userSearch.toLowerCase())
    )

    const selectedUser = users.find(
        (user) => user.id === formData.supervisor_user_id
    )

    return (
        <>
            {loading && <Loading />}
            <div className="container">
                <Aside />
                <main className="main-content">
                    <Navbar />

                    {/* Form Container */}
                    <div className="box p-0 ">
                        {/* Header */}
                        <div className="m-6 p-6 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600">
                            <h3 className=" text-white font-bold text-xl">
                                <FontAwesomeIcon
                                    icon={faBuilding}
                                    className="ml-2"
                                />
                                ویرایش سازمان
                            </h3>
                        </div>

                        {/* Form Content */}
                        <div className="p-6 space-y-6">
                            {/* Organization Name */}
                            <div className="flex flex-col w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    نام سازمان
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                                    required
                                />
                            </div>

                            {/* Logo Upload */}
                            <div className="flex flex-col w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    لوگو
                                </label>
                                <div className="flex items-center space-x-4 space-x-reverse">
                                    <div className="relative w-24 h-24 rounded-xl bg-gray-100 overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center">
                                        {logoPreview ? (
                                            <img
                                                src={logoPreview}
                                                alt="Logo"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faImage}
                                                className="text-gray-400 text-2xl"
                                            />
                                        )}
                                    </div>
                                    <label className="flex-1">
                                        <div className="px-4 py-3 bg-white border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors text-center">
                                            <span className="text-sm font-medium text-gray-700">
                                                {formData.logo
                                                    ? 'تغییر لوگو'
                                                    : 'آپلود لوگو'}
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleLogoChange}
                                                className="hidden"
                                            />
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Supervisor Select */}
                            <div className="flex flex-col w-full relative mb-96">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    مدیر مسئول
                                </label>
                                <div className="relative">
                                    <div
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl cursor-pointer flex items-center justify-between hover:border-indigo-500 transition-colors"
                                        onClick={() =>
                                            setShowUserDropdown(
                                                !showUserDropdown
                                            )
                                        }
                                    >
                                        <div className="flex items-center">
                                            {selectedUser ? (
                                                <>
                                                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center ml-3">
                                                        <FontAwesomeIcon
                                                            icon={faUser}
                                                            className="text-indigo-600"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">
                                                            {
                                                                selectedUser.full_name
                                                            }
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {selectedUser
                                                                ?.userDepartmentPosition[0]
                                                                ?.position
                                                                ?.name ||
                                                                'بدون عنوان'}
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <span className="text-gray-400">
                                                    انتخاب مدیر...
                                                </span>
                                            )}
                                        </div>
                                        <FontAwesomeIcon
                                            icon={faChevronDown}
                                            className={`transition-transform ${showUserDropdown ? 'transform rotate-180' : ''}`}
                                        />
                                    </div>

                                    {showUserDropdown && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-xl overflow-hidden">
                                            <div className="p-3 border-b border-gray-200 bg-gray-50">
                                                <div className="relative">
                                                    <FontAwesomeIcon
                                                        icon={faSearch}
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="جستجوی کاربر..."
                                                        className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                        value={userSearch}
                                                        onChange={(e) =>
                                                            setUserSearch(
                                                                e.target.value
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>
                                            <div className="max-h-60 overflow-y-auto">
                                                {filteredUsers.map((user) => (
                                                    <div
                                                        key={user.id}
                                                        className={`p-3 hover:bg-indigo-50 cursor-pointer flex items-center ${user.id === formData.supervisor_user_id ? 'bg-indigo-100' : ''}`}
                                                        onClick={() =>
                                                            handleUserSelect(
                                                                user
                                                            )
                                                        }
                                                    >
                                                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center ml-3">
                                                            <FontAwesomeIcon
                                                                icon={faUser}
                                                                className="text-indigo-600"
                                                            />
                                                        </div>
                                                        <div>
                                                            <div className="font-medium">
                                                                {user.full_name}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {user.email}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="flex justify-end space-x-3 space-x-reverse pt-6 maxLg:flex-col ">
                                <button
                                    type="button"
                                    onClick={() => navigate(-1)}
                                    className="px-6 maxLg:my-2 py-3 border border-gray-300 rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center"
                                >
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        className="ml-2"
                                    />
                                    انصراف
                                </button>
                                <button
                                    onClick={() => handleSubmit()}
                                    className="px-6 maxLg:my-2 py-3 rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg flex items-center"
                                >
                                    <FontAwesomeIcon
                                        icon={faSave}
                                        className="ml-2"
                                    />
                                    ذخیره تغییرات
                                </button>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    )
}

export default OrganizationEdit
